/**
 * TeacherToolbar - 教師モード用ツールバー
 * Dashboard.jsのヘッダー内に表示される教師専用コントロール
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { teacherAPI } from '../utils/api';
import { logClientError } from '../utils/safeLog';

function TeacherToolbar({
  presets,
  selectedPresetId,
  onPresetChange,
  onOpenPresetPanel,
  currentUser,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isStudentPanelOpen, setIsStudentPanelOpen] = useState(false);
  const [students, setStudents] = useState([]);
  const [inviteCode, setInviteCode] = useState(null);
  const [studentLoading, setStudentLoading] = useState(false);
  const [studentError, setStudentError] = useState('');

  // Load student management data
  const loadStudentData = useCallback(async () => {
    setStudentLoading(true);
    setStudentError('');
    try {
      const [codeRes, studentsRes] = await Promise.all([
        teacherAPI.getInviteCode().catch(() => ({ data: { code: null } })),
        teacherAPI.getMyStudents(),
      ]);
      setInviteCode(codeRes.data?.code || null);
      setStudents(studentsRes.data?.students || []);
    } catch (err) {
      logClientError('Failed to load student data:', err);
      setStudentError('teacher.loadStudentsFailed');
    } finally {
      setStudentLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isStudentPanelOpen) {
      loadStudentData();
    }
  }, [isStudentPanelOpen, loadStudentData]);

  const handleGenerateCode = async () => {
    try {
      const res = await teacherAPI.generateInviteCode();
      setInviteCode(res.data?.code || null);
    } catch (err) {
      logClientError('Failed to generate invite code:', err);
      setStudentError('teacher.generateCodeFailed');
    }
  };

  const handleRegenerateCode = async () => {
    if (!window.confirm(t('teacher.regenerateConfirm'))) return;
    try {
      const res = await teacherAPI.regenerateInviteCode();
      setInviteCode(res.data?.code || null);
    } catch (err) {
      logClientError('Failed to regenerate invite code:', err);
      setStudentError('teacher.regenerateCodeFailed');
    }
  };

  const handleCopyCode = () => {
    if (inviteCode) {
      navigator.clipboard.writeText(inviteCode).catch(() => {});
    }
  };

  const handleRemoveStudent = async (studentId, studentName) => {
    if (!window.confirm(t('teacher.removeStudentConfirm', { name: studentName }))) return;
    try {
      await teacherAPI.removeStudent(studentId);
      setStudents((prev) => prev.filter((s) => s.id !== studentId));
    } catch (err) {
      logClientError('Failed to remove student:', err);
      setStudentError('teacher.removeStudentFailed');
    }
  };

  return (
    <>
      <div
        className="flex items-center gap-3 flex-wrap"
        style={{
          padding: '10px 16px',
          borderRadius: '12px',
          background: 'rgba(255,255,255,0.15)',
        }}
      >
        {/* 教師バッジ */}
        <span
          className="rounded-full px-3 py-1 text-sm font-bold tracking-wider"
          style={{ background: 'var(--accent)', color: '#fff' }}
        >
          TEACHER
        </span>

        {/* プリセット選択 */}
        <div className="flex items-center gap-1.5">
          <label
            htmlFor="teacher-preset-select"
            className="text-sm font-semibold"
            style={{ color: 'rgba(253,251,246,0.8)' }}
          >
            {t('teacher.presetLabel')}
          </label>
          <select
            id="teacher-preset-select"
            value={selectedPresetId}
            onChange={(e) => onPresetChange(e.target.value)}
            className="rounded-lg border px-3 py-1.5 text-sm"
            style={{
              borderColor: 'rgba(255,255,255,0.3)',
              background: 'rgba(255,255,255,0.15)',
              color: 'var(--text-on-primary)',
              fontFamily: "'MFW-UDDigiKyokashoProN-Regular', 'UDデジタル教科書体', sans-serif",
            }}
          >
            <option value="" style={{ color: 'var(--text)', background: 'var(--bg-surface)' }}>
              {t('teacher.noPreset')}
            </option>
            {presets.map((p) => (
              <option
                key={p.id}
                value={String(p.id)}
                style={{ color: 'var(--text)', background: 'var(--bg-surface)' }}
              >
                {p.name} {p.is_default ? t('teacher.defaultMarker') : ''}
              </option>
            ))}
          </select>
        </div>

        {/* プリセット管理 */}
        <button
          onClick={onOpenPresetPanel}
          className="rounded-lg px-3.5 py-1.5 text-sm font-semibold transition-colors"
          style={{
            border: '1px solid rgba(255,255,255,0.4)',
            color: 'var(--text-on-primary)',
            background: 'transparent',
          }}
        >
          {t('teacher.managePresets')}
        </button>

        {/* 生徒管理 */}
        <button
          onClick={() => setIsStudentPanelOpen(true)}
          className="rounded-lg px-3.5 py-1.5 text-sm font-semibold transition-colors"
          style={{
            border: '1px solid rgba(255,255,255,0.4)',
            color: 'var(--text-on-primary)',
            background: 'transparent',
          }}
        >
          {t('teacher.manageStudents')}
        </button>

        {/* 分析ダッシュボード */}
        <button
          onClick={() => navigate('/teacher/dashboard')}
          className="rounded-lg px-3.5 py-1.5 text-sm font-semibold transition-colors"
          style={{
            border: '1px solid rgba(255,255,255,0.4)',
            color: 'var(--text-on-primary)',
            background: 'transparent',
          }}
        >
          {t('dashboard.menuAnalytics')}
        </button>
      </div>

      {/* 生徒管理モーダル */}
      {isStudentPanelOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm px-4"
          style={{ backgroundColor: 'rgba(30,45,56,0.4)' }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="teacher-students-title"
            className="w-full max-w-lg rounded-[28px] border p-6 max-h-[90vh] overflow-y-auto"
            style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-surface)',
              boxShadow: '0 24px 60px rgba(61,90,110,0.15)',
              fontFamily: "'MFW-UDDigiKyokashoProN-Regular', 'UDデジタル教科書体', sans-serif",
            }}
          >
            <div className="mb-6 flex items-center justify-between">
              <h2 id="teacher-students-title" className="text-2xl font-bold" style={{ color: 'var(--primary)' }}>
                {t('teacher.manageStudents')}
              </h2>
              <button
                onClick={() => setIsStudentPanelOpen(false)}
                className="text-3xl font-bold transition-colors"
                aria-label={t('common.close')}
                style={{ color: 'var(--text-muted)' }}
              >
                x
              </button>
            </div>

            {studentError && (
              <div
                role="alert"
                className="mb-4 rounded-2xl p-3 text-sm"
                style={{
                  border: '1px solid var(--danger)',
                  background: 'var(--danger-bg)',
                  color: 'var(--danger)',
                }}
              >
                {t(studentError)}
              </div>
            )}

            {studentLoading ? (
              <p className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
                {t('common.loading')}
              </p>
            ) : (
              <>
                {/* 招待コードセクション */}
                <div
                  className="rounded-2xl border p-4 mb-6"
                  style={{ borderColor: 'var(--border)', background: 'var(--bg-base)' }}
                >
                  <h3 className="text-sm font-bold mb-3" style={{ color: 'var(--text)' }}>
                    {t('teacher.inviteCode')}
                  </h3>
                  {inviteCode ? (
                    <div className="flex items-center gap-3">
                      <span
                        className="text-2xl font-bold tracking-[0.3em] px-4 py-2 rounded-lg select-all"
                        style={{
                          fontFamily: "'MFW-UDDigiKyokashoProN-Regular', 'UDデジタル教科書体', sans-serif",
                          background: 'var(--bg-muted)',
                          color: 'var(--primary)',
                          letterSpacing: '0.3em',
                        }}
                      >
                        {inviteCode}
                      </span>
                      <button
                        onClick={handleCopyCode}
                        className="rounded-lg border px-3 py-2 text-xs font-semibold transition-colors"
                        style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }}
                      >
                        {t('teacher.copy')}
                      </button>
                      <button
                        onClick={handleRegenerateCode}
                        className="rounded-lg border px-3 py-2 text-xs font-semibold transition-colors"
                        style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}
                      >
                        {t('teacher.regenerate')}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleGenerateCode}
                      className="rounded-lg px-4 py-2 text-sm font-semibold transition-colors"
                      style={{ background: 'var(--primary)', color: 'var(--text-on-primary)' }}
                    >
                      {t('teacher.generateCode')}
                    </button>
                  )}
                </div>

                {/* 生徒一覧セクション */}
                <div>
                  <h3 className="text-sm font-bold mb-3" style={{ color: 'var(--text)' }}>
                    {t('teacher.studentList')}
                  </h3>
                  {students.length === 0 ? (
                    <p className="text-center py-6 text-sm" style={{ color: 'var(--text-muted)' }}>
                      {t('teacher.noStudents')}
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" style={{ color: 'var(--text)' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid var(--border)' }}>
                            <th
                              className="text-left py-2 px-2 font-semibold text-xs"
                              style={{ color: 'var(--text-muted)' }}
                            >
                              {t('teacher.colName')}
                            </th>
                            <th
                              className="text-right py-2 px-2 font-semibold text-xs"
                              style={{ color: 'var(--text-muted)' }}
                            >
                              {t('teacher.colAction')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {students.map((student) => (
                            <tr
                              key={student.id}
                              style={{ borderBottom: '1px solid var(--border)' }}
                            >
                              <td className="py-2 px-2">{student.username}</td>
                              <td className="py-2 px-2 text-right">
                                <button
                                  onClick={() =>
                                    handleRemoveStudent(student.id, student.username)
                                  }
                                  className="rounded-lg border px-2 py-1 text-xs font-semibold transition-colors"
                                  style={{
                                    borderColor: 'var(--danger)',
                                    color: 'var(--danger)',
                                  }}
                                >
                                  {t('teacher.remove')}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default TeacherToolbar;
